import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  Invoice,
  CATEGORY_LABELS,
  TIPO_DISPLAY_NAMES,
  InvoiceCategory,
  InvoiceTipo,
} from "../types/invoice";
import { ApiService } from "../services/api";

interface EditInvoiceModalProps {
  open: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onUpdated: () => void;
}

const EditInvoiceModal: React.FC<EditInvoiceModalProps> = ({
  open,
  invoice,
  onClose,
  onUpdated,
}) => {
  const [nifEmitente, setNifEmitente] = useState("");
  const [nifAdquirente, setNifAdquirente] = useState("");
  const [valor, setValor] = useState("");
  const [dataEmissao, setDataEmissao] = useState<Dayjs | null>(null);
  const [categoria, setCategoria] = useState<InvoiceCategory | "">("");
  const [tipo, setTipo] = useState<InvoiceTipo | "">("");
  const [descricao, setDescricao] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (invoice && open) {
      setNifEmitente(invoice.nif_emitente || "");
      setNifAdquirente(invoice.nif_adquirente || "");
      setValor(invoice.valor != null ? String(Number(invoice.valor)) : "");
      setDataEmissao(invoice.data_emissao ? dayjs(invoice.data_emissao) : null);
      setCategoria(invoice.categoria || "");
      setTipo(invoice.tipo || "");
      setDescricao(invoice.descricao || "");
      setError(null);
    }
  }, [invoice, open]);

  const handleClose = () => {
    if (!submitting) onClose();
  };

  // Só os campos alterados são enviados e validados, para que faturas antigas com
  // dados inválidos (ex.: NIF emitente em falta) possam ser corrigidas campo a campo.
  const original = {
    nifEmitente: invoice?.nif_emitente || "",
    nifAdquirente: invoice?.nif_adquirente || "",
    valor: invoice?.valor != null ? String(Number(invoice.valor)) : "",
    dataEmissao: invoice?.data_emissao
      ? dayjs(invoice.data_emissao).format("YYYY-MM-DD")
      : "",
    categoria: invoice?.categoria || "",
    tipo: invoice?.tipo || "",
    descricao: invoice?.descricao || "",
  };
  const nifEmitenteChanged = nifEmitente !== original.nifEmitente;
  const nifAdquirenteChanged = nifAdquirente !== original.nifAdquirente;
  const nifEmitenteInvalid =
    nifEmitenteChanged && !/^\d{9}$/.test(nifEmitente);
  const nifAdquirenteInvalid =
    nifAdquirenteChanged &&
    nifAdquirente !== "" &&
    !/^\d{9}$/.test(nifAdquirente);
  const dataEmissaoValid =
    !!dataEmissao && dataEmissao.isValid() && !dataEmissao.isAfter(dayjs(), "day");

  const isValid =
    !nifEmitenteInvalid &&
    !nifAdquirenteInvalid &&
    parseFloat(valor) > 0 &&
    dataEmissaoValid &&
    categoria !== "";

  const handleSubmit = async () => {
    if (!isValid || !invoice || !dataEmissao) return;

    setSubmitting(true);
    setError(null);

    try {
      const updateData: Record<string, unknown> = {};
      if (nifEmitenteChanged) updateData.nif_emitente = nifEmitente;
      if (nifAdquirenteChanged) updateData.nif_adquirente = nifAdquirente || null;
      if (valor !== original.valor) updateData.valor = parseFloat(valor);
      const dataStr = dataEmissao.format("YYYY-MM-DD");
      if (dataStr !== original.dataEmissao) updateData.data_emissao = dataStr;
      if (categoria !== original.categoria) updateData.categoria = categoria;
      if (tipo !== original.tipo) updateData.tipo = tipo || null;
      if (descricao !== original.descricao) updateData.descricao = descricao;

      if (Object.keys(updateData).length > 0) {
        await ApiService.updateInvoice(invoice.id, updateData);
      }
      onClose();
      onUpdated();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Erro ao atualizar fatura";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Editar Fatura #{invoice?.id}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="NIF Emitente"
              value={nifEmitente}
              onChange={(e) =>
                setNifEmitente(e.target.value.replace(/\D/g, "").slice(0, 9))
              }
              required
              fullWidth
              error={nifEmitenteInvalid}
              helperText={
                nifEmitenteInvalid
                  ? "NIF deve ter 9 dígitos"
                  : !/^\d{9}$/.test(nifEmitente)
                  ? "NIF em falta ou inválido (opcional para guardar)"
                  : undefined
              }
            />
            <TextField
              label="NIF Adquirente"
              value={nifAdquirente}
              onChange={(e) =>
                setNifAdquirente(
                  e.target.value.replace(/\D/g, "").slice(0, 9)
                )
              }
              fullWidth
              error={nifAdquirenteInvalid}
              helperText={
                nifAdquirenteInvalid ? "NIF deve ter 9 dígitos" : undefined
              }
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Valor (EUR)"
              value={valor}
              onChange={(e) => {
                const v = e.target.value;
                if (/^\d*\.?\d{0,2}$/.test(v) || v === "") setValor(v);
              }}
              required
              fullWidth
              inputProps={{ inputMode: "decimal" }}
            />
            <DatePicker
              label="Data de Emissão"
              value={dataEmissao}
              onChange={setDataEmissao}
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                  error: !dataEmissaoValid,
                  helperText: !dataEmissaoValid
                    ? "Data inválida ou no futuro"
                    : undefined,
                },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Categoria"
              value={categoria}
              onChange={(e) =>
                setCategoria(e.target.value as InvoiceCategory)
              }
              select
              required
              fullWidth
            >
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as InvoiceTipo)}
              select
              fullWidth
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {Object.entries(TIPO_DISPLAY_NAMES).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || submitting}
        >
          {submitting ? <CircularProgress size={20} /> : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInvoiceModal;
